import microsoftlogo from 'assets/microsoftlogo.png';
import { FusionCustomInput } from 'components/index';
import BaseModal from 'components/Modals/BaseModal';
import SearchBar from 'components/SearchBar/SearchBar';
import type { Member, PermItem, RoleFormData } from 'features/Admin/Roles/types/Roles.types';
import UserTable from 'features/Admin/Users/UserTable/UserTable';
import type { WidgetData } from 'features/dashboard/types/widgetTypes';
import useSearch, { useSearchUpdate } from 'hooks/useSearch';
import { useEffect, useMemo, useState } from 'react';
import { Button, Container, Form } from 'react-bootstrap';
import { FaLock } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { selectUsername } from 'store/selectors/User/userSelectors';
import { safeJSONParse } from 'utils/data/parse.utils';
import { getMemberUserId, toNumberArray } from '../../../types/guards';

type SetState<T> = React.Dispatch<React.SetStateAction<T>>;

interface DetailsTabProps {
  formData?: RoleFormData;
  setFormData?: SetState<RoleFormData>;
  membersList?: Member[];
  setMembersList?: SetState<Member[]>;
  roleid?: number | string;
  edit?: boolean;
  onClearForm?: () => void;
  widgetData?: WidgetData;
  permList?: PermItem[];
  checks?: Record<string, boolean>;
}

const userMatchesSearch = (user: Member, search?: string) => {
  const normalizedSearch = (search || '').toLowerCase().replace(/\s/g, '');
  if (!normalizedSearch) return true;

  return [user.name, user.email, user.create_by_email, user.date_added]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().replace(/\s/g, '').includes(normalizedSearch));
};

const DetailsTab: React.FC<DetailsTabProps> = ({
  formData,
  setFormData,
  membersList,
  setMembersList,
  roleid,
  edit = true,
  onClearForm = () => {},
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const username = useSelector(selectUsername);
  const search = useSearch();
  const [, updateSearch] = useSearchUpdate() as [string, (newSearch: string) => void];
  const [toSelect, setToSelect] = useState<number[]>([]);
  const [userList, setUserList] = useState<Member[]>([]);

  useEffect(() => {
    if (!Array.isArray(membersList) || membersList.length === 0) {
      setUserList([]);
      return;
    }

    const filteredList = membersList.filter((member) => member.selected === true);
    setUserList(filteredList);
  }, [membersList]);

  const openModal = () => {
    setToSelect(toNumberArray((userList || []).map((u) => getMemberUserId(u))));
    setModalOpen(true);
  };

  const handleSubmit = () => {
    setMembersList?.((prev: Member[] = []) =>
      prev.map((member) => ({
        ...member,
        selected:
          typeof getMemberUserId(member) === 'number'
            ? toSelect.includes(getMemberUserId(member) as number)
            : false,
      })),
    );
    setModalOpen(false);
  };

  const handleRowClick = (row: Member | object) => {
    if (!row || typeof row !== 'object') return;
    const r = row as Member;
    const isMicrosoftUser = Boolean(r.disabled);
    if (isMicrosoftUser) return;

    const id =
      getMemberUserId(r) ?? (typeof r['id'] === 'number' ? (r['id'] as number) : undefined);
    if (typeof id !== 'number') return;

    if (toSelect.includes(id)) {
      setToSelect((prev) => prev.filter((i) => i !== id));
      return;
    }
    setToSelect((prev) => [...prev, id]);
  };

  const columns = useMemo(() => {
    return [
      {
        header: 'Users',
        accessorKey: 'name',
        Cell: ({
          row,
          cell,
        }: {
          row: { original: Member };
          cell: { getValue: () => string | number | undefined };
        }) => {
          const name = String(cell.getValue() ?? '');
          const activated = Number(row.original.activated ?? 0);
          const isMicrosoftUser = Boolean(row.original.disabled);
          const isLocked = Boolean(row.original.locked);
          if (isLocked) return <FaLock className="text-white opacity-50" />;
          return (
            <p className="text-white fs-6 text-wrap text-break d-flex gap-2 align-items-center">
              {isMicrosoftUser && (
                <span>
                  <img src={microsoftlogo} style={{ height: 20 }} alt="Microsoft" />
                </span>
              )}
              {name}
              {activated === 0 && <span className="text-danger">(pending)</span>}
            </p>
          );
        },
        size: 180,
      },
      {
        header: 'Email',
        accessorKey: 'email',
        Cell: ({
          row,
          cell,
        }: {
          row: { original: Member };
          cell: { getValue: () => string | number | undefined };
        }) => {
          const email = String(cell.getValue() ?? '');
          return <p className="text-white fs-6 text-wrap text-break">{email}</p>;
        },
        size: 200,
      },
      {
        header: 'Created by',
        accessorKey: 'create_by_email',
        Cell: ({
          row,
          cell,
        }: {
          row: { original: Member };
          cell: { getValue: () => string | number | undefined };
        }) => {
          const createdBy = String(cell.getValue() ?? '');
          if (Boolean(row.original.locked)) return <FaLock className="text-white opacity-50" />;
          return <p className="text-white fs-6 text-wrap text-break">{createdBy}</p>;
        },
        size: 200,
      },
      {
        header: 'Date added',
        accessorKey: 'date_added',
        Cell: ({
          row,
          cell,
        }: {
          row: { original: Member };
          cell: { getValue: () => string | number | undefined };
        }) => {
          const dateAdded = String(cell.getValue() ?? '');
          if (Boolean(row.original.locked)) return <FaLock className="text-white opacity-50" />;
          return <p className="text-white fs-6 text-wrap text-break">{dateAdded}</p>;
        },
        size: 150,
      },
    ];
  }, [roleid]);

  const rows = useMemo(() => {
    if (!Array.isArray(userList)) return []; //null checking

    return userList
      .map((user) => {
        const isLocked = user?.canModify === false;
        const msRolesParsed = safeJSONParse(user?.msRoles, []) as Array<string | number>;
        const disabled =
          Array.isArray(msRolesParsed) &&
          msRolesParsed.some((msRole: string | number) => Number(msRole) === Number(roleid));

        const resolvedUserId =
          getMemberUserId(user) ?? user?.id ?? user?.userId ?? user?.email ?? '';

        return {
          id: resolvedUserId,
          userId: resolvedUserId,
          name: isLocked ? '' : user?.name || '',
          email: user?.email || '',
          create_by_email: isLocked ? '' : user?.create_by_email || '',
          date_added: isLocked ? '' : new Date(user?.date_added || Date.now()).toLocaleString(),
          msRoles: user?.msRoles,
          activated: user?.activated ?? 0,
          disabled: isLocked || disabled,
          locked: isLocked,
        };
      })
      .filter((user) => userMatchesSearch(user as Member, search));
  }, [userList, roleid, search]);

  const modalRows = useMemo(() => {
    if (!Array.isArray(membersList)) return [];

    return membersList
      .filter((user) => user?.canModify !== false)
      .filter((user) => (user?.email || '') !== username)
      .map((user, index) => {
        const msRolesParsed = safeJSONParse(user?.msRoles, []) as Array<string | number>;
        const disabled = Array.isArray(msRolesParsed)
          ? msRolesParsed.some((msRole: string | number) => Number(msRole) === Number(roleid))
          : false;

        const resolvedUserId =
          getMemberUserId(user) ?? user?.userId ?? user?.id ?? user?.email ?? index;

        return {
          id: resolvedUserId,
          userId: resolvedUserId,
          name: user?.name || '',
          email: user?.email || '',
          create_by_email: user?.create_by_email || '',
          date_added: new Date(user?.date_added || Date.now()).toLocaleString(),
          msRoles: user?.msRoles,
          activated: user?.activated ?? 0,
          index: index,
          disabled,
        };
      })
      .filter((user) => userMatchesSearch(user as Member, search));
  }, [membersList, username, roleid, search]);

  return (
    <Container
      fluid
      className="d-flex flex-column w-100 gap-3 flex-grow-1"
      style={{ minHeight: 0 }}
    >
      <FusionCustomInput className="w-3xs" label="Role Name" disabled={!edit} required={true}>
        <Form.Control
          className="w-100 bg-transparent border-0"
          disabled={!edit}
          value={formData?.roleName || ''}
          onChange={(e) =>
            setFormData?.(
              (prev) =>
                ({
                  ...(prev ?? {
                    roleName: '',
                    members: [],
                    initialMembers: [],
                    permissions: [],
                    layout: [],
                  }),
                  roleName: e.target.value,
                }) as RoleFormData,
            )
          }
        />
      </FusionCustomInput>
      <div className="w-100 flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
        <div className="w-100 w-lg-25 mb-2 justify-content-end ms-auto">
          <SearchBar
            className="bg-primary-highlight rounded-3"
            inputClassName="text-white bg-primary-shadow rounded-3"
            value={search}
            onChange={(v: string) => updateSearch(v)}
            disabled={false}
          />
        </div>
        <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
          <UserTable
            rows={rows}
            columns={columns || []}
            customStyles={{
              cell: { borderBottom: '3px solid #E0E1E228' },
              header: { borderBottom: '3px solid #E0E1E228' },
              container: { flex: 1, minHeight: 0 },
            }}
            muiTablePaperProps={{
              sx: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
            }}
            muiTableContainerProps={{ sx: { flex: 1, overflow: 'auto' } }}
            customEmptyMessage={search ? 'No Users Match Search' : 'No Users in Role'}
          />
        </div>
      </div>
      {edit && (
        <div className="d-flex flex-column flex-lg-row justify-content-between w-100 gap-2">
          <Button className="p-3 rounded-2 w-100 w-lg-3xs" onClick={onClearForm}>
            Clear Form
          </Button>
          <Button className="p-3 rounded-2 w-100 w-lg-3xs" onClick={() => openModal()}>
            Add/Remove User(s)
          </Button>
        </div>
      )}
      <BaseModal
        show={modalOpen}
        handleShow={() => setModalOpen(false)}
        size="xl"
        style={{ '--bs-modal-width': 'min(92vw, 1320px)' } as React.CSSProperties}
      >
        <div className="d-flex flex-column" style={{ minHeight: 0 }}>
          <h1 className="fs-3 text-center text-sm-start flex-shrink-0 mb-2">
            Select User(s) to Add/Remove from Role
          </h1>
          <div className="w-100 w-lg-25 mb-2 justify-content-end ms-auto flex-shrink-0">
            <SearchBar
              className="bg-primary-highlight rounded-3"
              inputClassName="text-white bg-primary-shadow rounded-3"
              value={search}
              onChange={(v: string) => updateSearch(v)}
              disabled={false}
            />
          </div>
          <div className="d-flex flex-column" style={{ minHeight: 0, height: '65vh' }}>
            <UserTable
              columns={columns || []}
              rows={modalRows}
              customStyles={{
                cell: { borderBottom: '3px solid #E0E1E228' },
                header: { borderBottom: '3px solid #E0E1E228' },
                container: { flex: 1, minHeight: 0 },
              }}
              muiTablePaperProps={{
                sx: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
              }}
              muiTableContainerProps={{
                sx: { flex: 1, overflowY: 'auto', maxHeight: '60vh' },
                className: 'customScrollbar-Y',
              }}
              selectedRowIds={toSelect}
              onRowClick={handleRowClick}
            />
          </div>
          <div className="d-flex flex-column flex-lg-row w-100 mt-2 justify-content-between gap-2 flex-shrink-0">
            <Button
              className="py-3 rounded-2 border border-1 border-white w-100 w-lg-3xs"
              onClick={() => setModalOpen(false)}
              variant="dark"
            >
              Cancel
            </Button>
            <Button
              className="p-3 rounded-2 border border-1 border-white w-100 w-lg-3xs"
              onClick={() => handleSubmit()}
            >
              Submit
            </Button>
          </div>
        </div>
      </BaseModal>
    </Container>
  );
};

export default DetailsTab;
